import { geocode } from '../../services/mapboxService';
import { isGeneralOrTraumaHospital, parseEpJourneys, type EpJourneyResearch, type EpRouteCandidate } from '../../functions/src/common/epAdvanceV2';
import { collectEpJourneyRoutes } from './epSurveyRoutes';

export type EpSurveyV2Research = {
    coordinates: { latitude: number; longitude: number } | null;
    geocodeExact: boolean;
    routes: EpRouteCandidate[];
    journeys: EpJourneyResearch[];
    hospitals: Array<{ name: string; address: string; url: string }>;
    hospitalReview: Array<{ name: string; address: string; url: string }>;
    police: Array<{ name: string; address: string; url: string }>;
    fireStations: Array<{ name: string; address: string; url: string }>;
    pharmacies: Array<{ name: string; address: string; url: string }>;
    sourceErrors: string[];
};

export async function collectEpSurveyV2Research(intake: Record<string, string>): Promise<EpSurveyV2Research> {
    const result: EpSurveyV2Research = { coordinates: null, geocodeExact: false, routes: [], journeys: [], hospitals: [], hospitalReview: [], police: [],
        fireStations: [], pharmacies: [], sourceErrors: [] };
    const venue = [intake.venueAddress, intake.venueState, intake.venueCountry].filter(Boolean).join(', ');
    const journeys = parseEpJourneys(intake.criticalJourneys || '', intake.routeOrigin || '', venue);
    result.journeys = journeys.map(journey => ({ ...journey, routes: [] }));
    try {
        const location = await geocode(venue);
        result.coordinates = { latitude: location.lat, longitude: location.lng };
        result.geocodeExact = location.isExact;
        if (!location.isExact)
            result.sourceErrors.push('Venue geocode is approximate.');
    } catch {
        result.sourceErrors.push('Google Maps could not geocode the venue.');
    }
    try {
        const { Route } = await google.maps.importLibrary('routes') as google.maps.RoutesLibrary;
        for (let index = 0; index < journeys.length; index++) {
            const { routes, errors } = await collectEpJourneyRoutes(journeys[index], request => Route.computeRoutes(request));
            result.journeys[index].routes = routes;
            const unresolved = errors.filter(error => routes.length < 4 || error.includes('geocoding is approximate'));
            if (unresolved.length)
                result.sourceErrors.push(`Journey ${index + 1} routing: ${unresolved.join('; ')}. ${routes.length} of 4 distinct routes mapped.`);
        }
        result.routes = result.journeys[0]?.routes || [];
    } catch {
        result.sourceErrors.push('Google Routes candidates are unavailable.');
    }
    if (result.coordinates) {
        try {
            const library = await google.maps.importLibrary('places') as any;
            const center = { lat: result.coordinates.latitude, lng: result.coordinates.longitude };
            const searches = await Promise.allSettled(([
                ['hospitals', 'hospital'], ['police', 'police'], ['fireStations', 'fire_station'], ['pharmacies', 'pharmacy'],
            ] as const).map(async ([key, type]) => {
                const response = await library.Place.searchNearby({ fields: ['displayName', 'formattedAddress', 'googleMapsURI', 'primaryType'],
                    locationRestriction: { center, radius: key === 'hospitals' ? 25000 : 10000 }, includedPrimaryTypes: [type], maxResultCount: key === 'hospitals' ? 20 : 5,
                    rankPreference: library.SearchNearbyRankPreference.DISTANCE });
                return { key, places: (response.places || []).map((place: any) => ({
                    name: String(place.displayName || ''), address: String(place.formattedAddress || ''),
                    url: String(place.googleMapsURI || ''), primaryType: String(place.primaryType || ''),
                })) };
            }));
            for (let index = 0; index < searches.length; index++) {
                const search = searches[index];
                if (search.status === 'fulfilled') {
                    if (search.value.key === 'hospitals') {
                        result.hospitals = search.value.places.filter((place: { name: string; primaryType: string }) => isGeneralOrTraumaHospital(place.name, place.primaryType)).slice(0, 5);
                        result.hospitalReview = search.value.places.filter((place: { name: string; primaryType: string }) => !isGeneralOrTraumaHospital(place.name, place.primaryType)).slice(0, 10);
                    } else
                        result[search.value.key] = search.value.places.slice(0, 5);
                }
                else
                    result.sourceErrors.push(`${['Hospital', 'Police', 'Fire station', 'Pharmacy'][index]} search unavailable.`);
            }
        } catch {
            result.sourceErrors.push('Google Places searches are unavailable.');
        }
    }
    return result;
}
